import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { convertCmToInches, getObjectKeys } from '@vestico/utils';

import {
  HeightUnit,
  SizeChartItem,
  SizeChartMeasurementOption,
  SizeChartMeasurementOptions,
  SizesChartSize,
  stringForSizeChartMeasurementOption,
} from '@vestico/model';

import globalStyle from '@vestico/shared/src/layouts/utils.module.css';

import style from './SizeRecommendationTabSizeChartItems.module.css';

interface SizeChartItemDisplayProps {
  size?: SizesChartSize;
  last?: boolean;
  unit: HeightUnit;
}

const SizeChartItemDisplay = ({ size, unit, last }: SizeChartItemDisplayProps) => {
  const { min, max } = size ?? { min: 0, max: 0 };
  const minValue = useMemo(() => {
    switch (unit) {
      case HeightUnit.centimeter:
        return min;
      case HeightUnit.feet:
        return convertCmToInches(min);
    }
    throw new Error(`Unhandled height unit ${unit}`);
  }, [unit, min]);
  const maxValue = useMemo(() => {
    switch (unit) {
      case HeightUnit.centimeter:
        return max;
      case HeightUnit.feet:
        return convertCmToInches(max);
    }
    throw new Error(`Unhandled height unit ${unit}`);
  }, [unit, max]);

  const content = useMemo(() => {
    if (!size) {
      return null;
    }
    if (minValue === maxValue) {
      return <div>{minValue}</div>;
    }
    return (
      <>
        <div>{minValue}</div>
        <div className={clsx(style.sep)}>–</div>
        <div>{maxValue}</div>
      </>
    );
  }, [maxValue, minValue, size]);

  return <div className={clsx(style.item, style.size, { [style.end]: last })}>{content}</div>;
};

export interface SizeRecommendationTabSizeChartProps {
  unit: HeightUnit;
  title?: string;
  items: SizeChartItem[];
  // eslint-disable-next-line react/no-unused-prop-types
  flipped?: boolean;
  description?: string;
}

export const SizeRecommendationTabSizeChartItems = ({ unit, title, items, description }: SizeRecommendationTabSizeChartProps) => {
  const { t } = useTranslation('shared');

  const sanitizedDescription = useMemo(() => {
    const trimmed = description?.trim();
    if (trimmed && trimmed.length > 0) {
      return trimmed;
    }
    return undefined;
  }, [description]);

  const availableSizeChartMeasurements = useMemo(() => {
    const firstItem = items[0];
    if (!firstItem) {
      return [];
    }
    const predefinedValues = SizeChartMeasurementOptions.filter((option) => !!firstItem[option]);
    const additionalValues = getObjectKeys(firstItem).filter(
      (itemKey) =>
        !!firstItem[itemKey] && !Array.isArray(firstItem[itemKey]) && !SizeChartMeasurementOptions.includes(itemKey as SizeChartMeasurementOption)
    );

    const availableValues = [...predefinedValues, ...additionalValues];
    return availableValues;
  }, [items]);

  const headerRowValues = useMemo(() => {
    const values = availableSizeChartMeasurements.map((measurement) => {
      const title = stringForSizeChartMeasurementOption(measurement as SizeChartMeasurementOption, t);
      return title;
    });

    return [title ?? t('LOC_SIZE_CHART_TITLE_SIZE'), ...values];
  }, [availableSizeChartMeasurements, t, title]);

  const headerRows = useMemo(() => {
    return (
      <tr>
        {headerRowValues.map((v) => (
          <th key={v}>{v}</th>
        ))}
      </tr>
    );
  }, [headerRowValues]);

  const rows = useMemo(() => {
    return items.map((item, index) => {
      const sizeValue = item.sizeValues.join(' | ');
      return (
        // eslint-disable-next-line react/no-array-index-key
        <tr key={index}>
          <td>{sizeValue}</td>
          {availableSizeChartMeasurements.map((measurement, index) => {
            return (
              <td key={measurement}>
                <SizeChartItemDisplay
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  size={item[measurement] as SizesChartSize}
                  unit={unit}
                  last={index === availableSizeChartMeasurements.length - 1}
                />
              </td>
            );
          })}
        </tr>
      );
    });
  }, [availableSizeChartMeasurements, items, unit]);

  if (availableSizeChartMeasurements.length === 0) {
    return null;
  }

  return (
    <div className={clsx(style.root)}>
      <div className={clsx(style.container)}>
        <table className={clsx(style.table)}>
          <thead>{headerRows}</thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
      {sanitizedDescription ? (
        <div className={clsx(style.description)}>
          <p className={clsx(globalStyle.textSmall)}>{sanitizedDescription}</p>
        </div>
      ) : null}
    </div>
  );
};
